import { toast } from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

/** How often to refetch the surfacing list(s) while a row's image is generating. */
const POLL_INTERVAL_MS = 15_000;

/**
 * Stop waiting for a generation after this long. The job has no pollable id
 * (the endpoint fires an async Inngest event and returns immediately), so we
 * detect completion by watching the featured image and cap the wait here.
 */
const GENERATION_TIMEOUT_MS = 5 * 60_000;

/** Non-blocking message shown on the row when a generation doesn't land. */
export const GENERATION_TIMEOUT_MESSAGE =
  "Generation didn't finish — check the attraction's gallery";

export interface UseAttractionImageGenerationOptions {
  /**
   * The attraction's current featured-image id (or `null` when it has none).
   * Completion is detected when this value changes from what it was at kickoff.
   */
  currentImageId: string | null;
  /**
   * Query keys to invalidate on each poll tick so the surfacing view refetches
   * and picks up the newly generated featured image. Each entry is a full query
   * key (e.g. `['Attraction']` or `['TourAttractionOrder', tourId]`).
   */
  invalidateKeys: readonly (readonly unknown[])[];
}

export interface UseAttractionImageGenerationResult {
  /** True while a generation is in flight (kicked off, not yet landed/timed out). */
  isGenerating: boolean;
  /** True when the last generation exceeded the timeout without landing. */
  hasTimedOut: boolean;
  /** Call after the generation job is kicked off to begin polling for the result. */
  startGeneration: () => void;
  /** Clear the timed-out state (e.g. when the user dismisses the error badge). */
  dismissTimeout: () => void;
}

/**
 * Drives the "generate an attraction's main photo from a list" state machine.
 *
 * The generate-images endpoint returns immediately and does the work async, so
 * there is no job id to poll. Instead, once {@link startGeneration} is called we
 * poll the surfacing list(s) on an interval and treat the attraction's featured
 * image id changing as success. If the image never changes within the timeout we
 * surface a dismissible error state instead of hanging forever.
 *
 * Extracted so both the attraction-list image cell and the tour-stops list row
 * share identical kickoff / polling / timeout behavior.
 */
export function useAttractionImageGeneration({
  currentImageId,
  invalidateKeys,
}: UseAttractionImageGenerationOptions): UseAttractionImageGenerationResult {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  // Featured-image id captured at kickoff; completion = this id changing.
  const baselineImageIdRef = useRef<string | null>(null);
  // Keep the latest keys in a ref so re-renders (e.g. an inline array literal)
  // don't restart the polling interval and, crucially, don't reset the timeout.
  const invalidateKeysRef = useRef(invalidateKeys);
  invalidateKeysRef.current = invalidateKeys;

  // While generating, poll the list(s) on an interval and give up after a cap.
  useEffect(() => {
    if (!isGenerating) return;

    const interval = setInterval(() => {
      for (const queryKey of invalidateKeysRef.current) {
        void queryClient.invalidateQueries({ queryKey: [...queryKey] });
      }
    }, POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      setIsGenerating(false);
      setHasTimedOut(true);
      toast.error(GENERATION_TIMEOUT_MESSAGE);
    }, GENERATION_TIMEOUT_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isGenerating, queryClient]);

  // Completion: the featured image id changed since we kicked off.
  useEffect(() => {
    if (!isGenerating) return;
    if (currentImageId !== baselineImageIdRef.current) {
      setIsGenerating(false);
      toast.success('New photo added');
    }
  }, [isGenerating, currentImageId]);

  const startGeneration = () => {
    baselineImageIdRef.current = currentImageId;
    setHasTimedOut(false);
    setIsGenerating(true);
  };

  const dismissTimeout = () => setHasTimedOut(false);

  return { isGenerating, hasTimedOut, startGeneration, dismissTimeout };
}
